import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, X, Mail } from 'lucide-react'
import {
  createAssignment,
  updateAssignment,
  deleteAssignment,
  type Assignment,
  type Employee,
} from '../../lib/api'
import { handoffStep, removalHidesTask } from './handoff'

export const ROLE_LABEL: Record<string, string> = {
  account_manager: 'Account manager',
  lead: 'Lead',
  contributor: 'Contributor',
  reviewer: 'Reviewer',
}

const ROLES_BY_ENTITY: Record<string, string[]> = {
  client: ['account_manager', 'lead', 'contributor', 'reviewer'],
  project: ['lead', 'contributor', 'reviewer'],
  task: ['lead', 'contributor', 'reviewer'],
}

/** Order assignments so leads / account managers sort first. */
function leadFirst(a: Assignment, b: Assignment): number {
  const rank = (r: string) => (r === 'account_manager' || r === 'lead' ? 0 : 1)
  return rank(a.role) - rank(b.role)
}

interface AssigneeControlProps {
  entityType: 'client' | 'project' | 'task'
  entityId: string
  assignments: Assignment[]
  employees: Employee[]
  /**
   * Shows add/remove. Pass `roleCanManage` for client/project assignments
   * (admin-only on the API) and `roleCanWrite` for tasks, where a member may
   * reassign work they're on.
   */
  canManage?: boolean
  /** compact = inline chips (task rows); full = card rows (Team tab). */
  compact?: boolean
  /** Extra query keys to invalidate on change (e.g. ['tasks']). */
  invalidateKeys?: string[]
  /**
   * The signed-in user's employee id. Enables the task hand-off rules in
   * `./handoff.ts`: handing the Lead to someone keeps you on as Contributor.
   */
  currentEmployeeId?: string | null
  /**
   * The signed-in role only sees tasks it's assigned to (`!roleCanSeeAll`), so
   * removing your own last seat is confirmed — the task would leave your list.
   */
  scopedToAssigned?: boolean
}

export function AssigneeControl({
  entityType,
  entityId,
  assignments,
  employees,
  canManage = true,
  compact = false,
  invalidateKeys = [],
  currentEmployeeId = null,
  scopedToAssigned = false,
}: AssigneeControlProps) {
  const qc = useQueryClient()
  const roles = ROLES_BY_ENTITY[entityType] ?? ['contributor']
  const [adding, setAdding] = useState(false)
  const [empId, setEmpId] = useState('')
  const [role, setRole] = useState(roles[0] ?? 'contributor')
  const [error, setError] = useState<string | null>(null)

  const empMap = new Map(employees.map((e) => [e.id, e]))

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['assignments'] })
    qc.invalidateQueries({ queryKey: ['events'] })
    invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }))
  }

  const add = useMutation({
    mutationFn: async () => {
      // Work out the hand-off before the new seat exists — the add must land
      // first, while the giver is still on the task and allowed to assign.
      const step = entityType === 'task'
        ? handoffStep(assignments, { currentEmployeeId, newEmployeeId: empId, newRole: role })
        : null
      await createAssignment({ employeeId: empId, entityType, entityId, role })
      if (step?.kind === 'demote') await updateAssignment(step.assignmentId, { role: 'contributor' })
      if (step?.kind === 'drop') await deleteAssignment(step.assignmentId)
    },
    onSuccess: () => {
      invalidate()
      setAdding(false)
      setEmpId('')
      setError(null)
    },
    onError: (err) => {
      invalidate()
      setError(err instanceof Error ? err.message : 'Could not assign')
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteAssignment(id),
    onSuccess: () => {
      invalidate()
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not remove'),
  })

  const confirmRemove = (a: Assignment) => {
    const hides =
      entityType === 'task' &&
      removalHidesTask(assignments, a, { currentEmployeeId, scoped: scopedToAssigned })
    if (hides && !confirm('Remove yourself from this task? It will leave your task list.')) return
    remove.mutate(a.id)
  }

  const sorted = [...assignments].sort(leadFirst)
  const active = employees.filter((e) => e.active)

  return (
    <div className={`assignees${compact ? ' assignees--compact' : ''}`}>
      {sorted.length === 0 && !adding && (
        <span className="assignees__empty">{compact ? 'Unassigned' : 'No one assigned yet'}</span>
      )}

      {sorted.map((a) => {
        const emp = empMap.get(a.employeeId)
        return (
          <span
            key={a.id}
            className={`assignee-chip${compact ? ' assignee-chip--compact' : ''}`}
            title={`${emp?.name ?? 'Unknown'} · ${ROLE_LABEL[a.role] ?? a.role}`}
          >
            <span
              className="assignee-chip__avatar"
              style={{ background: emp?.avatarColor ? `${emp.avatarColor}22` : undefined, color: emp?.avatarColor }}
            >
              {emp?.initials ?? '?'}
            </span>
            {!compact && (
              <span className="assignee-chip__text">
                <span className="assignee-chip__name">{emp?.name ?? 'Unknown'}</span>
                <span className="assignee-chip__role">{ROLE_LABEL[a.role] ?? a.role}</span>
              </span>
            )}
            {!compact && emp?.email && (
              <a href={`mailto:${emp.email}`} className="assignee-chip__mail" title={emp.email}>
                <Mail size={13} />
              </a>
            )}
            {canManage && (
              <button
                type="button"
                className="assignee-chip__remove"
                onClick={() => confirmRemove(a)}
                disabled={remove.isPending}
                aria-label={`Remove ${emp?.name ?? 'assignee'}`}
              >
                <X size={compact ? 11 : 13} />
              </button>
            )}
          </span>
        )
      })}

      {canManage &&
        (adding ? (
          <span className="assignee-add">
            <select value={empId} onChange={(e) => setEmpId(e.target.value)} aria-label="Teammate">
              <option value="">Teammate…</option>
              {active.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
              {roles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r] ?? r}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary btn-xs"
              disabled={!empId || add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending ? '…' : 'Add'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => {
                setAdding(false)
                setError(null)
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button type="button" className="assignee-add-btn" onClick={() => setAdding(true)}>
            <UserPlus size={compact ? 12 : 14} />
            {!compact && <span>Assign</span>}
          </button>
        ))}

      {error && (
        <span className="assignees__error" role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/** Read-only overlapping avatar stack for cards (board/list). */
export function AssigneeAvatars({
  assignments,
  employees,
  max = 3,
}: {
  assignments: Assignment[]
  employees: Employee[]
  max?: number
}) {
  if (assignments.length === 0) return null
  const empMap = new Map(employees.map((e) => [e.id, e]))
  const sorted = [...assignments].sort(leadFirst)
  const shown = sorted.slice(0, max)
  const extra = sorted.length - shown.length

  return (
    <span className="avatar-stack">
      {shown.map((a) => {
        const emp = empMap.get(a.employeeId)
        return (
          <span
            key={a.id}
            className="avatar-stack__item"
            style={{ background: emp?.avatarColor ? `${emp.avatarColor}22` : undefined, color: emp?.avatarColor }}
            title={`${emp?.name ?? 'Unknown'} · ${ROLE_LABEL[a.role] ?? a.role}`}
          >
            {emp?.initials ?? '?'}
          </span>
        )
      })}
      {extra > 0 && <span className="avatar-stack__item avatar-stack__more">+{extra}</span>}
    </span>
  )
}
